/**
 * Sort order for the server-rendered governance action lists.
 *
 * The same two orders the interactive explorer offers, so the control on a hub
 * page is the one people already know. The explorer passes them to the API as
 * `Newly Created` / `Soon to Expire`; here they are URL slugs, because a hub
 * page has to be sortable without JavaScript for the sorted view to exist for
 * anyone who cannot run it.
 */

import type { IndexedProposal } from './proposal';

export type SortSlug = 'newest' | 'expiry';

/**
 * `{ label, value }`, matching what `RadioWithLabel` expects — the radio's
 * checked state compares the option it is given against the group's value, so
 * the two must be the same objects out of this one array.
 */
export const SORT_OPTIONS: { value: SortSlug; label: string }[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'expiry', label: 'Soon to expire' },
];

export function parseSort(value?: string | null): SortSlug {
  return value === 'expiry' ? 'expiry' : 'newest';
}

export function sortOption(slug: SortSlug) {
  return (
    SORT_OPTIONS.find((option) => option.value === slug) || SORT_OPTIONS[0]
  );
}

export function sortLabel(slug: SortSlug): string {
  return sortOption(slug).label;
}

const time = (value?: string | null) =>
  value ? new Date(value).getTime() : null;

export function sortProposals(
  proposals: IndexedProposal[],
  slug: SortSlug
): IndexedProposal[] {
  const sorted = [...proposals];

  if (slug === 'expiry') {
    // Soonest expiry first, and proposals with no expiry stamp last rather
    // than at epoch zero, where `|| 0` would have put them.
    return sorted.sort((a, b) => {
      const aTime = time(a.expireAt?.time);
      const bTime = time(b.expireAt?.time);
      if (aTime === null) return bTime === null ? 0 : 1;
      if (bTime === null) return -1;
      return aTime - bTime;
    });
  }

  return sorted.sort(
    (a, b) => (time(b.createdAt?.time) || 0) - (time(a.createdAt?.time) || 0)
  );
}
